import {
  DataGridObjectFilterModel
} from '@views/secured/beyond-smart-reports/views/report/components/devextreme/model/data-grid-object-filter.model';
import { FilterOperationDialogEnum } from '@views/secured/beyond-smart-reports/enums/filter-operation-dialog.enum';
import {
  DataGridCellObjectModel
} from '@views/secured/beyond-smart-reports/views/report/components/devextreme/model/data-grid-cell-object.model';
import { convertToDate } from '@base/utils/date.util';
import { Moment } from 'moment-timezone';
import { DevExtremeFieldTypesEnum } from '@views/secured/beyond-smart-reports/enums/dev-extreme-field-types.enum';
import { DxDataGridComponent } from 'devextreme-angular';
import { CustomFieldModel } from '@base/modules/rest/custom-field/model/custom-field.model';

export function applyConditionalFormattingFilters(gridCell: DataGridCellObjectModel, filters: DataGridObjectFilterModel[]): DataGridObjectFilterModel {
  for (const item of filters) {
    if (gridCell.column.dataField !== item.name) {
      continue;
    }
    if (!item.operator && !item.value) {
      continue;
    }
    let includesNew: string | boolean | number;
    if (item.fieldType === 'string' && gridCell.value) {
      includesNew = generateExpression(gridCell.value, item.value, item.operator, item.fieldType);
    } else if (item.fieldType === 'number' && gridCell.value) {
      includesNew = generateExpression(gridCell.value.toString(), item.value.toString(), item.operator, item.fieldType);
    } else if (item.fieldType === 'boolean' && gridCell.value) {
      includesNew = generateExpression(gridCell.value, item.value, item.operator, item.fieldType);
    } else if (item.fieldType === 'datetime' && gridCell.value) {
      includesNew = generateExpression(gridCell.value, item.value, item.operator, item.fieldType);
    } else if (item.fieldType === 'date' && gridCell.value) {
      includesNew = generateExpression(gridCell.value, item.value, item.operator, item.fieldType);
    } else if (item.fieldType === 'select') {
      includesNew = generateExpression(gridCell.value, item.value, item.operator, item.fieldType);
    } else if (item.fieldType === 'decimal_number') {
      includesNew = generateExpression(gridCell.value.toString(), item.value.toString(), item.operator, item.fieldType);
    }

    if (includesNew) {
      return item;
    }
  }
  return null;
}

function generateExpression(field: string, value: string, operator: FilterOperationDialogEnum, dataType: DevExtremeFieldTypesEnum): string | boolean {
  let includes: string | boolean;
  switch (operator) {
    case FilterOperationDialogEnum.EQUALS:
      if (dataType === 'date' || dataType === 'datetime') {
        const dates = parseDate(dataType, value, field);
        includes = dates.field === dates.value;
      } else {
        includes = field === value;
      }
      break;
    case FilterOperationDialogEnum.CONTAINS:
      if (dataType === 'date' || dataType === 'datetime') {
        const dates_ = parseDate(dataType, value, field);
        includes = dates_.field.includes(dates_.value);
      } else {
        includes = field.includes(value);
      }
      break;
    case FilterOperationDialogEnum.DOES_NOT_CONTAIN:
      if (dataType === 'date' || dataType === 'datetime') {
        const d = parseDate(dataType, value, field);
        includes = !d.field.includes(d.value);
      } else {
        includes = !field.includes(value);
      }
      break;
    case FilterOperationDialogEnum.STARTS_WITH:
      includes = field?.startsWith(value);
      break;
    case FilterOperationDialogEnum.ENDS_WITH:
      includes = field.endsWith(value);
      break;
    case FilterOperationDialogEnum.LESS_THAN:
      includes = Number(field) < Number(value);
      break;
    case FilterOperationDialogEnum.GREATER_THAN:
      includes = Number(field) > Number(value);
      break;
    case FilterOperationDialogEnum.LESS_EQUALS_THAN:
      includes = Number(field) <= Number(value);
      break;
    case FilterOperationDialogEnum.GREATER_EQUALS_THAN:
      includes = Number(field) >= Number(value);
      break;
    case FilterOperationDialogEnum.NOT_EQUALS:
      if (dataType === 'date' || dataType === 'datetime') {
        const dates__ = parseDate(dataType, value, field);
        includes = dates__.field !== dates__.value;
      } else {
        includes = field !== value;
      }
      break;
    case FilterOperationDialogEnum.HAS_VALUE:
      includes = field === value;
      break;
    case FilterOperationDialogEnum.DOES_NOT_HAVE_VALUE:
      includes = field !== value;
      break;
  }
  return includes;
}

function parseDate(dataType: string, value: any, field: any): { value: any; field: any } {
  if (dataType === 'datetime') {
    const t: Moment = convertToDate(value);
    value = t.date() + '.' + (t.month() + 1) + '.' + t.year() + ' ' + (t.hours() - 2) + ':' + t.minutes();
    const timeField: Moment = convertToDate(field);
    field = timeField.date() + '.' + (timeField.month() + 1) + '.' + timeField.year() + ' ' + (timeField.hours() - 2) + ':' + timeField.minutes();
  } else if (dataType === 'date') {
    const t: Moment = convertToDate(value);
    value = t.year() + '-' + ('0' + (t.month() + 1)).slice(-2) + '-' + ('0' + t.date()).slice(-2);
    const t2= convertToDate(field);
    field = t2.year() + '-' + ('0' + (t2.month() + 1)).slice(-2) + '-' + ('0' + t2.date()).slice(-2);
  }
  return {value, field};
}

export function generateConditionalFormatting(event: DataGridCellObjectModel, filters: DataGridObjectFilterModel[]): void {
  const formattingFilter = applyConditionalFormattingFilters(event, filters);
  if (formattingFilter) {
    if (!formattingFilter.isRowColoring) {
      addCssStyles(event.cellElement, formattingFilter)
    }

    if (formattingFilter.isRowColoring) {
      setTimeout(() => {
        const cellElemnt = event.cellElement as HTMLElement;
        const parentRow = cellElemnt.closest('tr');
        if (parentRow) {
          const filtersWithRowColoring = filters.filter(f => f.isRowColoring);
          const lastFilter = filtersWithRowColoring[filtersWithRowColoring.length - 1];
          const hasClass = parentRow.classList.contains('colored-row');

          if (!hasClass || hasClass && event.column.dataField === lastFilter.name) {
            addCssStyles(parentRow, formattingFilter);
            if (!hasClass) {
              parentRow.classList.add('colored-row');
            }
          }
        }
      });
    }
  }
}

function addCssStyles(element: any, formattingFilter: DataGridObjectFilterModel): void {
  element.style.fontStyle = 'normal';
  element.style.fontWeight = 'normal';
  element.style.textDecoration = 'none';
  element.style.backgroundColor = formattingFilter.highlightColor ? rgbaToHex(formattingFilter.highlightColor.r, formattingFilter.highlightColor.g, formattingFilter.highlightColor.b, formattingFilter.highlightColor.a) : null;
  element.style.color = formattingFilter.color ? rgbaToHex(formattingFilter.color.r, formattingFilter.color.g, formattingFilter.color.b, formattingFilter.color.a) : null;
  element.style.fontStyle = `${formattingFilter?.textStyle}`;
  element.style.textDecoration = `${formattingFilter?.textStyle}`;
  element.style.fontWeight = `${formattingFilter?.textStyle}`;
}

function rgbaToHex(r: number, g: number, b: number, a: number): string {
  const outParts = [
    r.toString(16),
    g.toString(16),
    b.toString(16),
    Math.round(a * 255).toString(16).substring(0, 2),
  ];

  outParts.forEach((part: string, i: number) => {
    if (part.length === 1) {
      outParts[i] = '0' + part;
    }
  });

  return ('#' + outParts.join(''));
}

export function convertDataType(fields: CustomFieldModel[], dataField: string): string {
  const field = fields?.find(f => f.dbName === dataField);
  switch (field?.type) {
    case 'NUMBER':
      return 'number';
    case 'SELECT':
      return 'select';
    case 'TEXT':
    case 'LOOKUP':
    case 'TIME':
      return 'string';
    case 'DECIMAL_NUMBER':
      return 'decimal_number';
    case 'DATE':
      return 'date';
    case 'DATE_TIME':
      return 'datetime';
    case 'SWITCHER':
      return 'boolean';
  }
  return 'string';
}

export function getRowByIndex(index: number, dataGrid: DxDataGridComponent): HTMLElement | null {
  const rowElement = dataGrid.instance.getRowElement(index);

  if (rowElement && rowElement.length > 0) {
    return rowElement[0] as HTMLElement;
  }
  return null;
}
